import { Hidden } from '@mui/material'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { ContentPagination } from 'src/components/ContentPagination/ContentPagination'
import { Footer } from 'src/components/Footer/Footer'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { PremiumModal } from 'src/components/global/PremiumModal'
import { SearchField } from 'src/components/global/SearchField'
import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
import { SearchPagination } from 'src/components/SearchPagination/SearchPagination'
import { SolutionSearchPageTab } from 'src/components/Tabs/SolutionSearchPageTab'
import { useGetTopicsForHeaderQuery } from 'src/graphql/generated/hooks'
import { useDecodedKeyword } from 'src/hooks/useGetDecodeKeyword'
// import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, SEARCH_CONTENTS_TAB, SEARCH_DIRECTORY_TAB, TaxonomyType } from 'src/types'
import { getContentFilters, getDirectoryFilters } from 'src/utils/getFilters'

type Props = {
  filters: FilterOption[]
  contentFilters: FilterOption[]
}

const SearchPage: NextPage<Props> = ({ filters, contentFilters }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialFilters, setInitialFilters] = useState<FilterOption[]>(filters)

  const router = useRouter()
  const lastUpdatedKeyword = useRef<string | null>(null)
  const { updateQueryParams } = useUpdateQueryParams()
  const tabs = router.query.tabs
  const pathName = tabs ? tabs[0] : 'directory'
  const meta = (
    <Meta title="Search legaltech products" description="Search legaltech products" url={`/search/${pathName}`} />
  )
  const [showModal, setShowModal] = useState(false)
  const [tabIdx, setTabIdx] = useState(SEARCH_DIRECTORY_TAB)
  const [filterHeight, setFilterHeight] = useState(false)

  const searchKeyword = useDecodedKeyword(router.query.keyword)
  // const { session } = useAuthenticated()
  // const filtersDisabled = useMemo(() => !session?.user?.isPremium, [session])

  const { data } = useGetTopicsForHeaderQuery({ fetchPolicy: 'cache-first' })
  const decodedSearchKeyword = useDecodedKeyword(router.query.keyword)
  // const decodedQSSort = typeof router.query.sort === 'string' ? decodeURIComponent(router.query.sort) : ''

  const onSelectTab = (index: number) => {
    setTabIdx(index)
    let newTabs = 'directory'
    let defaultSort = 'Default'
    if (index === 401) {
      newTabs = 'directory'
      defaultSort = 'Default'
    }
    if (index === 402) {
      newTabs = 'contents'
      defaultSort = 'Chronologically'
    }
    const keyword = router.query.keyword
    defaultSort = decodedSearchKeyword ? 'bykeyword' : defaultSort

    updateQueryParams({
      tabs: newTabs,
      sort: defaultSort,
      page: undefined,
      keyword: (keyword as string | undefined) || undefined,
    })
    // On tab switch: remove all filters, keep only tabs and sort
    // router.replace(`/search?tabs=${newTabs}&sort=${defaultSort}`, undefined, { shallow: true })
  }

  const isSponsorLevel = initialFilters.some((item, index) => {
    if (item.title === TaxonomyType.SponsorLevel) {
      initialFilters[index].data = initialFilters[index].data.sort((a, b) => {
        if (a.level && b.level) {
          return a.level - b.level
        }
        return 0
      })
    }
    return item.title === TaxonomyType.SponsorLevel
  })

  useEffect(() => {
    const { tabs } = router.query
    if (tabs && tabs === 'directory') {
      setTabIdx(SEARCH_DIRECTORY_TAB)
    }
    if (tabs && tabs === 'contents') {
      setTabIdx(SEARCH_CONTENTS_TAB)
    }
  }, [router.isReady])

  useEffect(() => {
    if (!data || !router.query.keyword) return

    const keyword = decodeURIComponent(router.query.keyword as string).toLowerCase()

    if (lastUpdatedKeyword.current?.toLowerCase?.() === keyword && data) return

    const productType: string[] = []

    if (data?.topics?.some((item) => item?.name.toLowerCase() === keyword)) {
      data.topics
        .filter((item) => item?.name.toLowerCase() === keyword)
        .forEach((item) => {
          item?.subTopics?.forEach((subTopic) => {
            productType.push(item.documentId + '@@@@' + subTopic?.documentId)
          })
        })

      updateQueryParams({ productType: productType.join(',') })
    } else {
      data?.topics?.forEach((item) => {
        item?.subTopics
          .filter((subTopic) => subTopic?.name.toLowerCase() === keyword)
          .forEach((subTopic) => {
            updateQueryParams({
              productType: item.documentId + '@@@@' + subTopic?.documentId,
            })
          })
      })
    }

    lastUpdatedKeyword.current = keyword
  }, [router.query.keyword, data])

  return (
    <ReCaptcha>
      <Main meta={meta}>
        <MainContainer id="mainContainer">
          <ThemeOnly theme="ilta">
            <IltaHeader />
          </ThemeOnly>
          <div className="flex flex-col items-center justify-center bg-clouds px-5 md:px-0">
            <div className="!max-w-[1100px] w-full py-2">
              <h1 className="pt-[0.5rem] pb-[0.625rem] text-[24px] md:text-[30px] font-bold font-secondary text-darkgray">
                {tabIdx === SEARCH_DIRECTORY_TAB && 'Search Results'}
                {tabIdx === SEARCH_CONTENTS_TAB && 'Search Contents'}
              </h1>
            </div>
            <ThemeOnly theme="legaltech">
              <div className="!max-w-[1100px] w-full py-2">
                <div className="md:w-1/4 pr-10">
                  <SearchField className="mb-[23px]" />
                </div>
              </div>
            </ThemeOnly>
          </div>
          <ThemeOnly theme="legaltech">
            <div className="flex w-full bg-clouds justify-center items-center">
              <div className="!max-w-[1100px] w-full flex flex-row justify-center items-center">
                <SolutionSearchPageTab
                  id={SEARCH_DIRECTORY_TAB}
                  activeTab={tabIdx}
                  className="w-[117px]"
                  onSelectTab={onSelectTab}
                >
                  Directory
                </SolutionSearchPageTab>
                <SolutionSearchPageTab
                  id={SEARCH_CONTENTS_TAB}
                  activeTab={tabIdx}
                  className="w-[117px]"
                  onSelectTab={onSelectTab}
                >
                  Contents
                </SolutionSearchPageTab>
              </div>
            </div>
          </ThemeOnly>
          <hr />
          {tabIdx === SEARCH_DIRECTORY_TAB && (
            <div className="flex flex-row justify-center w-full md:px-0 px-5">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32 relative">
                <Hidden mdDown>
                  <div className="w-1/4 pr-10 flex flex-col">
                    <ThemeOnly theme="ilta">
                      <SearchField className="my-8" />
                    </ThemeOnly>
                    <Sidebar
                      filterOptions={initialFilters}
                      defaultSelectFilterIdx={isSponsorLevel ? 3 : 1}
                      filterHeight={filterHeight}
                    />
                  </div>
                </Hidden>
                <SearchPagination
                  filterProps={initialFilters}
                  followOnly={false}
                  includeCompany={true}
                  setFilterHeight={setFilterHeight}
                  searchKeyword={searchKeyword}
                />
              </div>
            </div>
          )}

          {tabIdx === SEARCH_CONTENTS_TAB && (
            <div className="flex flex-row justify-center w-full md:pl-0 md:pr-0 pl-[20px] pr-[20px]">
              <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
                <Hidden mdDown>
                  <div className="w-1/4 pr-10 flex flex-col">
                    <Sidebar
                      filterOptions={contentFilters}
                      // filtersDisabled={filtersDisabled}
                      defaultSelectFilterIdx={1}
                      onClickWhenDisabled={() => setShowModal(true)}
                    />
                  </div>
                </Hidden>
                <PremiumModal isOpen={showModal} onClose={() => setShowModal(false)} />
                <ContentPagination
                  filterProps={contentFilters}
                  // filtersDisabled={filtersDisabled}
                  onClickWhenDisabled={() => setShowModal(true)}
                  searchKeyword={searchKeyword}
                />
              </div>
            </div>
          )}

          <ThemeOnly theme="legaltech">
            <Footer />
          </ThemeOnly>
        </MainContainer>
      </Main>
    </ReCaptcha>
  )
}

export default SearchPage

export const getStaticProps: GetStaticProps<Props> = async ({}) => {
  try {
    const filters = await getDirectoryFilters()

    const contentFiltersOptions = {
      subjectsFilter: { includeSubjectMatters: true, includeThemes: false },
      authorsFilter: { includeAuthors: true, includeExperts: true },
    }
    const contentFilters: FilterOption[] = await getContentFilters(contentFiltersOptions)

    return {
      props: {
        filters,
        contentFilters,
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Search:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 5, // Reattempt fetching during the next static regeneration
    }
  }
}

export async function getStaticPaths() {
  return {
    paths: [
      // String variant:
      // '/search/',
      // '/search/directory',
      // '/search/contents',
      // Object variant:
      { params: { tabs: [] } },
      { params: { tabs: ['directory'] } },
      { params: { tabs: ['contents'] } },
    ],
    fallback: false,
  }
}
